/** Runtime decoder for the additive option-evidence authoring capability. */
import { z } from "zod";

export const WATCH_OPTION_EVIDENCE_CAPABILITY_SCHEMA_VERSION = 1 as const;

const MODES = ["HIDDEN", "SHADOW", "NOTIFY"] as const;
const BASE_SUBJECTS = ["POSITION_EXIT", "STRUCTURE_ENTRY"] as const;

export type WatchOptionEvidenceMode = typeof MODES[number];
export type WatchOptionEvidenceBaseSubject = typeof BASE_SUBJECTS[number];

export interface WatchOptionEvidenceCapability {
  readonly schema_version: typeof WATCH_OPTION_EVIDENCE_CAPABILITY_SCHEMA_VERSION;
  readonly available: boolean;
  readonly customer_visible: boolean;
  readonly mode: WatchOptionEvidenceMode;
  readonly logic: readonly "ALL"[];
  readonly base_subjects: readonly WatchOptionEvidenceBaseSubject[];
  readonly unavailable_reason?: string;
}

const safeText = z.string().trim().min(1).max(280)
  .refine((value) => Array.from(value).every((character) => {
    const point = character.codePointAt(0) ?? 0;
    return point >= 32 && point !== 127;
  }));

const capabilitySchema = z.object({
  schema_version: z.literal(WATCH_OPTION_EVIDENCE_CAPABILITY_SCHEMA_VERSION),
  available: z.boolean(),
  customer_visible: z.boolean(),
  mode: z.enum(MODES),
  logic: z.array(z.literal("ALL")).length(1),
  base_subjects: z.array(z.enum(BASE_SUBJECTS)).min(1).max(BASE_SUBJECTS.length)
    .superRefine((subjects, context) => {
      if (new Set(subjects).size !== subjects.length) {
        context.addIssue({ code: "custom", message: "Duplicate option-evidence base subject." });
      }
    }),
  unavailable_reason: safeText.optional(),
}).strict();

/**
 * Decode the complete server-owned capability from an untrusted contracts
 * payload. Missing, partial, unknown, and extended schemas all fail closed.
 */
export function decodeWatchOptionEvidenceCapability(
  value: unknown,
): WatchOptionEvidenceCapability | null {
  const result = capabilitySchema.safeParse(value);
  return result.success ? result.data : null;
}

/**
 * Return whether exact option-evidence authoring is explicitly customer-live
 * for a base Watch subject. Catalog and presenter gates remain separate and
 * must also pass before the UI advertises an individual signal.
 */
export function isWatchOptionEvidenceAuthoringAvailable(
  capability: WatchOptionEvidenceCapability | null,
  baseSubject: WatchOptionEvidenceBaseSubject,
): boolean {
  return capability?.available === true
    && capability.customer_visible === true
    && capability.mode === "NOTIFY"
    && capability.logic.length === 1
    && capability.logic[0] === "ALL"
    && capability.base_subjects.includes(baseSubject);
}
